import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, Building2, CircleCheck, Inbox, Sparkles, TriangleAlert, Users } from "lucide-mui";
import { api, type MasterCompanyStats, type PlatformHealth } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { EnrichmentFunnelPanel, CountryCoveragePanel } from "../../components/dashboard/platform/CompanyCoveragePanels.tsx";
import DataProviderUsagePanel from "../../components/dashboard/platform/DataProviderUsagePanel.tsx";
import GetLeadsFairUsePanel from "../../components/dashboard/platform/GetLeadsFairUsePanel.tsx";
import { InferencePanel } from "../../components/dashboard/platform/InferencePanel.tsx";
import { CompanyDataPanel, PeopleAndCrmPanel } from "../../components/dashboard/platform/ImportedDataPanels.tsx";
import {
  NEEDS_ATTENTION,
  SIGNAL_STAT_DOMAINS,
  statRows,
  sumOf,
} from "../../components/dashboard/platform/importStats.ts";
import JobAdsPanel from "../../components/dashboard/platform/JobAdsPanel.tsx";
import JobHeatmapPanel from "../../components/dashboard/platform/JobHeatmapPanel.tsx";
import { ConnectionsPanel, RecentUploadsPanel } from "../../components/dashboard/platform/OperationsPanels.tsx";
import SourceHealthPanel from "../../components/dashboard/platform/SourceHealthPanel.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import KpiTile from "../../components/KpiTile.tsx";
import { formatNumber, percent } from "../../utils/format.ts";

/**
 * The shared data platform: what every import brought in and whether it is
 * current, how far master companies got through enrichment, contact-data
 * usage and the infrastructure behind it: the local inference rigs and the
 * background jobs. The signal engine has its own tab.
 * Site-wide staff only (`platform:read`).
 */
export default function PlatformOverviewPage() {
  // Pushed when dashboard_stats (every minute) or the import counts (every 15 min)
  // refresh; run logs live.
  const stats = useQuery({
    queryKey: keys.masterCompany.stats(),
    queryFn: api.getMasterCompanyStats,
    meta: { live: true },
  });
  const health = useQuery({
    queryKey: keys.platform.health(),
    queryFn: api.getPlatformHealth,
    meta: { live: true },
  });
  // Pushed with every five-minute sample; the rigs' own load also polls.
  const queues = useQuery({
    queryKey: keys.platform.queues(),
    queryFn: api.getPlatformQueues,
    refetchInterval: 60_000,
    meta: { live: true },
  });
  // Pushed on every tick (throttled): each job's runs per hour.
  const recurring = useQuery({
    queryKey: keys.platform.recurring(),
    queryFn: api.getPlatformRecurring,
    meta: { live: true },
  });

  // A failed health load leaves the company pipeline; errors toast globally.
  const loading = stats.isLoading || health.isLoading;

  return (
    <>
      {loading ? (
        <DashboardSkeleton />
      ) : !stats.data ? (
        <EmptyState
          variant="panel"
          icon={<Building2 />}
          title="Platform stats are unavailable"
          description="The company counts couldn't be loaded. Try again in a minute."
        />
      ) : (
        <Stack spacing={2.5}>
          <KpiRow stats={stats.data!} health={health.data ?? null} />
          {queues.data && <InferencePanel queues={queues.data} />}

          {health.data && <SourceHealthPanel sources={health.data.sources} />}

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 5 }}>
              <EnrichmentFunnelPanel stats={stats.data!} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }} sx={{ position: "relative", minHeight: { md: 320 } }}>
              <CountryCoveragePanel stats={stats.data!} />
            </Grid>
          </Grid>

          {health.data && (
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <CompanyDataPanel stats={health.data.stats} refreshedAt={health.data.stats_refreshed_at} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2.5}>
                  <PeopleAndCrmPanel stats={health.data.stats} refreshedAt={health.data.stats_refreshed_at} />
                  <JobAdsPanel stats={health.data.stats} />
                </Stack>
              </Grid>
            </Grid>
          )}

          <GetLeadsFairUsePanel />
          <DataProviderUsagePanel />

          {recurring.data && <JobHeatmapPanel jobs={recurring.data.jobs} />}

          {health.data && (
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ConnectionsPanel health={health.data} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RecentUploadsPanel imports={health.data.csv_imports} />
              </Grid>
            </Grid>
          )}
        </Stack>
      )}
    </>
  );
}

function KpiRow({ stats, health }: { stats: MasterCompanyStats; health: PlatformHealth | null }) {
  const countries = stats.companies_by_country.length;
  const waiting = stats.signaling.reduce((n, s) => n + s.inbox_pending, 0);
  const signalsIn24h = health
    ? Object.values(SIGNAL_STAT_DOMAINS).reduce(
        (n, where) => n + sumOf(statRows(health.stats, where.domain, where.sources), "added_24h"),
        0,
      )
    : stats.signaling.reduce((n, s) => n + s.records_added_24h, 0);

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Companies"
          icon={<Building2 />}
          value={stats.total_companies}
          delta={stats.companies_added_24h}
          deltaLabel="24h"
          caption={`${percent(stats.companies_with_website, stats.total_companies)} have a website · ${formatNumber(countries)} countries`}
        >
          <LinearProgress
            variant="determinate"
            value={stats.total_companies ? (stats.companies_with_website / stats.total_companies) * 100 : 0}
            sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover" }}
          />
        </KpiTile>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        {health ? (
          <PeopleTile health={health} />
        ) : (
          <KpiTile
            label="Classified"
            icon={<Sparkles />}
            value={stats.companies_classified}
            delta={stats.companies_classified_24h}
            deltaLabel="24h"
            caption={`${percent(stats.companies_classified, stats.websites_crawled)} of crawled companies`}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Signal records · 24h"
          icon={<Inbox />}
          value={signalsIn24h}
          caption={waiting ? `${formatNumber(waiting)} waiting for the model` : "Nothing waiting for the model"}
          to="/platform/signals"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        {health ? (
          <ImportsTile health={health} />
        ) : (
          <KpiTile
            label="Job ads"
            icon={<Briefcase />}
            value={stats.total_job_ads}
            delta={stats.job_ads_added_24h}
            deltaLabel="24h"
            caption={`${percent(stats.job_ads_matched, stats.total_job_ads)} matched to a company`}
            to="/job-ads"
          />
        )}
      </Grid>
    </Grid>
  );
}

function PeopleTile({ health }: { health: PlatformHealth }) {
  const people = statRows(health.stats, "contact", ["person"]);
  const roles = statRows(health.stats, "relation");
  return (
    <KpiTile
      label="People"
      icon={<Users />}
      value={sumOf(people, "total")}
      delta={sumOf(people, "added_24h")}
      deltaLabel="24h"
      caption={`${formatNumber(sumOf(roles, "matched"))} active roles`}
    />
  );
}

function ImportsTile({ health }: { health: PlatformHealth }) {
  const failed = health.sources.filter((s) => s.status === "failed").length;
  const attention = health.sources.filter((s) => NEEDS_ATTENTION.includes(s.status)).length;
  const upToDate = health.sources.filter((s) => s.status === "ok" || s.status === "running").length;
  return (
    <KpiTile
      label="Imports needing attention"
      icon={attention ? <TriangleAlert /> : <CircleCheck />}
      color={failed ? "error" : attention ? "warning" : "success"}
      emphasis={attention > 0}
      value={attention}
      caption={`${formatNumber(upToDate)} of ${formatNumber(health.sources.length)} up to date`}
    />
  );
}

function DashboardSkeleton() {
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2.5}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Skeleton variant="rounded" height={148} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={360} />
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Skeleton variant="rounded" height={340} />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Skeleton variant="rounded" height={340} />
        </Grid>
      </Grid>
    </Stack>
  );
}
